import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  CalendarClock, User, MapPin, Clock, AlertTriangle, Sparkles,
  X, Loader2, UserPlus, RefreshCw,
} from 'lucide-react';
import { onsiteApiFetch } from '../config/api.js';
import { useAuth } from '../contexts/AuthContext.js';
import { formatTime, formatDate } from '../lib/utils.js';
import type { OnsiteJob, OnsiteTechnician } from '../../../api/src/types/onsite-types.js';

interface BoardEntry {
  tech_id: string;
  tech_name: string;
  jobs: OnsiteJob[];
}

interface BoardResponse {
  date: string;
  board: BoardEntry[];
  unassigned: OnsiteJob[];
}

interface RecommendedTech {
  tech_id: string;
  tech_name: string;
  score?: number;
  reasoning?: string;
}

const PRIORITY_BADGE: Record<string, string> = {
  low:    'bg-secondary text-foreground',
  normal: 'bg-secondary text-foreground',
  high:   'bg-amber-500/20 text-amber-400',
  urgent: 'bg-red-500/20 text-red-400',
};

const STATUS_DOT: Record<string, string> = {
  new:        'bg-purple-500',
  scheduled:  'bg-blue-500',
  dispatched: 'bg-cyan-500',
  en_route:   'bg-amber-500',
  on_site:    'bg-green-500',
  completed:  'bg-secondary',
  invoiced:   'bg-emerald-500',
  closed:     'bg-secondary',
  cancelled:  'bg-red-600',
};

type DispatchKind = 'assign' | 'reassign';

interface DispatchModalState {
  kind: DispatchKind;
  job: OnsiteJob;
}

export default function DispatchPage() {
  const { isDispatcher } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().substring(0, 10));
  const [selectedJob, setSelectedJob] = useState<OnsiteJob | null>(null);
  const [dispatchModal, setDispatchModal] = useState<DispatchModalState | null>(null);
  const [recommendFor, setRecommendFor] = useState<OnsiteJob | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['dispatch', 'board', selectedDate],
    queryFn: () => onsiteApiFetch<BoardResponse>(`/api/v1/onsite/dispatch/board?date=${selectedDate}`),
    enabled: isDispatcher,
  });

  if (!isDispatcher) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-muted-foreground">Dispatch board is only available to dispatchers and admins.</p>
      </div>
    );
  }

  const board = data?.board ?? [];
  const unassigned = data?.unassigned ?? [];

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left: Unassigned / all jobs queue */}
      <div className="flex w-80 shrink-0 flex-col border-r border-border bg-card">
        <div className="border-b border-border p-4">
          <h2 className="font-semibold text-foreground">Dispatch Board</h2>
          <input
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            className="mt-2 w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
          />
          <p className="mt-1.5 text-xs text-muted-foreground">{formatDate(selectedDate)}</p>
        </div>

        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {unassigned.length > 0 && (
              <div>
                <h3 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-amber-400">
                  Unassigned ({unassigned.length})
                </h3>
                <div className="space-y-2">
                  {unassigned.map(job => (
                    <JobQueueCard
                      key={job.id}
                      job={job}
                      selected={selectedJob?.id === job.id}
                      onSelect={() => setSelectedJob(job)}
                      onAssign={() => setDispatchModal({ kind: 'assign', job })}
                      onRecommend={() => setRecommendFor(job)}
                    />
                  ))}
                </div>
              </div>
            )}

            <div>
              {unassigned.length > 0 && (
                <h3 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Assigned
                </h3>
              )}
              <div className="space-y-2">
                {board.flatMap(t => t.jobs).map(job => (
                  <JobQueueCard
                    key={job.id}
                    job={job}
                    selected={selectedJob?.id === job.id}
                    onSelect={() => setSelectedJob(job)}
                    onReassign={() => setDispatchModal({ kind: 'reassign', job })}
                    onRecommend={() => setRecommendFor(job)}
                  />
                ))}
              </div>
            </div>

            {board.length === 0 && unassigned.length === 0 && (
              <div className="flex flex-col items-center gap-2 p-8 text-muted-foreground">
                <CalendarClock className="h-8 w-8 text-muted-foreground" />
                <p className="text-sm font-medium">No jobs for this date</p>
                <p className="text-xs text-muted-foreground text-center">Select a different date or check with your dispatcher.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Right: Tech timeline */}
      <div className="flex-1 overflow-y-auto p-6">
        {selectedJob ? (
          <JobDetailPanel job={selectedJob} onClose={() => setSelectedJob(null)} />
        ) : (
          <TechTimeline board={board} />
        )}
      </div>

      {/* Dispatch modal */}
      {dispatchModal && (
        <DispatchModal
          kind={dispatchModal.kind}
          job={dispatchModal.job}
          onClose={() => setDispatchModal(null)}
        />
      )}

      {/* Recommend panel */}
      {recommendFor && (
        <RecommendPanel
          job={recommendFor}
          onClose={() => setRecommendFor(null)}
          onPick={techId => {
            setRecommendFor(null);
            setDispatchModal({ kind: recommendFor.assigned_tech_id ? 'reassign' : 'assign', job: recommendFor });
            // let modal open; user can pick the tech
            void techId;
          }}
        />
      )}
    </div>
  );
}

function JobQueueCard({
  job, selected, onSelect, onAssign, onReassign, onRecommend,
}: {
  job: OnsiteJob;
  selected: boolean;
  onSelect: () => void;
  onAssign?: () => void;
  onReassign?: () => void;
  onRecommend: () => void;
}) {
  return (
    <div
      className={`w-full rounded-lg border p-3 text-left transition-colors ${
        selected ? 'border-primary/50 bg-primary/10' : 'border-border bg-secondary hover:border-border'
      }`}
    >
      <button onClick={onSelect} className="block w-full text-left">
        <div className="flex items-start justify-between gap-2 mb-1">
          <p className="text-sm font-medium text-foreground line-clamp-1">{job.title}</p>
          <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-xs ${PRIORITY_BADGE[job.priority]}`}>
            {job.priority}
          </span>
        </div>
        <p className="text-xs text-muted-foreground line-clamp-1">{job.customer_name}</p>
        <div className="mt-1.5 flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${STATUS_DOT[job.status]}`} />
          <span className="text-xs text-muted-foreground">{job.status.replace('_', ' ')}</span>
          <span className="ml-auto text-xs text-muted-foreground">{formatTime(job.scheduled_start)}</span>
        </div>
      </button>

      <div className="mt-2 flex gap-1.5">
        {onAssign && (
          <button
            onClick={onAssign}
            className="flex items-center gap-1 rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
          >
            <UserPlus className="h-3 w-3" />
            Assign
          </button>
        )}
        {onReassign && (
          <button
            onClick={onReassign}
            className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-background hover:text-foreground"
          >
            <RefreshCw className="h-3 w-3" />
            Reassign
          </button>
        )}
        <button
          onClick={onRecommend}
          className="flex items-center gap-1 rounded border border-primary/30 bg-primary/10 px-2 py-1 text-xs text-primary hover:bg-primary/20"
        >
          <Sparkles className="h-3 w-3" />
          AI
        </button>
      </div>
    </div>
  );
}

function DispatchModal({ kind, job, onClose }: { kind: DispatchKind; job: OnsiteJob; onClose: () => void }) {
  const qc = useQueryClient();
  const [techId, setTechId] = useState<string>(job.assigned_tech_id ?? '');
  const [startsAt, setStartsAt] = useState<string>(() => job.scheduled_start?.substring(0, 16) ?? '');
  const [endsAt, setEndsAt] = useState<string>(() => job.scheduled_end?.substring(0, 16) ?? '');
  const [conflictWarning, setConflictWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: techsData } = useQuery({
    queryKey: ['technicians'],
    queryFn: () => onsiteApiFetch<{ technicians: OnsiteTechnician[] }>('/api/v1/onsite/technicians'),
  });
  const techs = techsData?.technicians ?? [];

  const submit = useMutation({
    mutationFn: () => {
      const path = kind === 'assign' ? '/api/v1/onsite/dispatch/assign' : '/api/v1/onsite/dispatch/reassign';
      return fetch(
        (import.meta.env.VITE_ONSITE_API_URL ?? 'https://api.theonestack.com') + path,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            job_id: job.id,
            tech_id: techId,
            scheduled_start: new Date(startsAt).toISOString(),
            scheduled_end: new Date(endsAt).toISOString(),
          }),
        }
      ).then(async res => {
        if (res.status === 401) {
          window.location.href = 'https://app.theonestack.com/login?redirect=onsite';
          throw new Error('Not authenticated');
        }
        if (res.status === 409) {
          const body = await res.json().catch(() => ({}));
          const techName = techs.find(t => t.id === techId)?.name ?? 'This technician';
          const msg = body?.error ?? body?.message
            ? (body?.error ?? body?.message)
            : `${techName} already has a job at this time. Choose a different time or technician.`;
          setConflictWarning(msg);
          throw new Error('Conflict');
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({ error: res.statusText }));
          throw new Error(body.error ?? `Request failed: ${res.status}`);
        }
        return res.json();
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dispatch', 'board'] });
      onClose();
    },
    onError: e => {
      if ((e as Error).message !== 'Conflict') setError((e as Error).message);
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setConflictWarning(null);
    setError(null);
    // Validate time ordering before submitting
    if (startsAt && endsAt && new Date(startsAt).getTime() >= new Date(endsAt).getTime()) {
      setError('End time must be after start time');
      return;
    }
    submit.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-4" onClick={e => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-foreground">
            {kind === 'assign' ? 'Assign Job' : 'Reassign Job'}
          </h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mb-3 text-sm text-muted-foreground">
          {job.title} <span className="text-muted-foreground/80">· {job.customer_name}</span>
        </p>

        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Technician</label>
            <select
              value={techId}
              onChange={e => setTechId(e.target.value)}
              required
              className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
            >
              <option value="">Select technician...</option>
              {techs.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Start</label>
              <input
                type="datetime-local"
                value={startsAt}
                onChange={e => setStartsAt(e.target.value)}
                required
                className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">End</label>
              <input
                type="datetime-local"
                value={endsAt}
                onChange={e => setEndsAt(e.target.value)}
                required
                className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
              />
            </div>
          </div>

          {conflictWarning && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{conflictWarning}</span>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-border py-2 text-sm text-muted-foreground hover:bg-secondary">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submit.isPending || !techId || !startsAt || !endsAt}
              className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-primary py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {kind === 'assign' ? 'Assign' : 'Reassign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RecommendPanel({ job, onClose, onPick }: {
  job: OnsiteJob;
  onClose: () => void;
  onPick: (techId: string) => void;
}) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['dispatch', 'recommend', job.id],
    queryFn: () =>
      onsiteApiFetch<{ recommendations: RecommendedTech[] }>(
        `/api/v1/onsite/dispatch/recommend-tech`,
        { method: 'POST', body: JSON.stringify({ job_id: job.id }) }
      ),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl border border-border bg-card p-4" onClick={e => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
            <Sparkles className="h-4 w-4 text-primary" />
            AI Tech Recommendations
          </h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mb-3 text-sm text-muted-foreground">Best matches for: {job.title}</p>

        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : isError ? (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            AI assistant unavailable: {(error as Error).message}
          </div>
        ) : (
          <div className="space-y-2">
            {(data?.recommendations ?? []).map((r, i) => (
              <div key={r.tech_id} className="rounded-lg border border-border bg-secondary p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <span className="rounded-full bg-primary/20 px-1.5 py-0.5 text-xs text-primary">#{i + 1}</span>
                      {r.tech_name}
                      {r.score != null && <span className="text-xs text-muted-foreground">(score {r.score.toFixed(1)})</span>}
                    </p>
                    {r.reasoning && (
                      <p className="mt-1 text-xs text-muted-foreground">{r.reasoning}</p>
                    )}
                  </div>
                  <button
                    onClick={() => onPick(r.tech_id)}
                    className="rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                  >
                    Pick
                  </button>
                </div>
              </div>
            ))}
            {(data?.recommendations ?? []).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">No recommendations available.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function TechTimeline({ board }: { board: BoardEntry[] }) {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-foreground">Technician Schedule</h2>
      {board.map(tech => (
        <div key={tech.tech_id} className="rounded-xl border border-border bg-card p-4">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary">
              <User className="h-4 w-4 text-foreground" />
            </div>
            <div>
              <p className="font-medium text-foreground">{tech.tech_name}</p>
              <p className="text-xs text-muted-foreground">{tech.jobs.length} job{tech.jobs.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="space-y-2">
            {tech.jobs.map(job => (
              <Link
                key={job.id}
                to={`/jobs/${job.id}`}
                className="flex items-center gap-3 rounded-lg border border-border bg-secondary p-3 hover:bg-secondary"
              >
                <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${STATUS_DOT[job.status]}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground line-clamp-1">{job.title}</p>
                  <p className="text-xs text-muted-foreground">{job.customer_name}</p>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatTime(job.scheduled_start)}
                </span>
              </Link>
            ))}
          </div>
        </div>
      ))}

      {board.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-muted-foreground">
          <CalendarClock className="h-12 w-12" />
          <p className="font-medium">No jobs scheduled</p>
          <p className="text-sm text-muted-foreground text-center max-w-xs">Assign jobs to technicians using the panel on the left and they'll appear in the timeline here.</p>
        </div>
      )}
    </div>
  );
}

function JobDetailPanel({ job, onClose }: { job: OnsiteJob; onClose: () => void }) {
  return (
    <div className="max-w-lg">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground">{job.title}</h2>
          <p className="text-sm text-muted-foreground">Ticket #{job.psa_ticket_number}</p>
        </div>
        <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:text-foreground">✕</button>
      </div>

      <div className="space-y-3 text-sm">
        <Row icon={User} label="Customer" value={job.customer_name} />
        <Row icon={MapPin} label="Address" value={job.customer_address} />
        <Row icon={User} label="Technician" value={job.assigned_tech_name} />
        <Row icon={Clock} label="Scheduled" value={`${formatTime(job.scheduled_start)} – ${formatTime(job.scheduled_end)}`} />
        {job.priority === 'urgent' && (
          <div className="flex items-center gap-2 rounded-lg bg-red-500/10 px-3 py-2 text-red-400">
            <AlertTriangle className="h-4 w-4" />
            <span>Urgent priority</span>
          </div>
        )}
        {job.notes && (
          <div className="rounded-lg bg-secondary p-3">
            <p className="mb-1 text-xs text-muted-foreground">Notes</p>
            <p className="text-foreground">{job.notes}</p>
          </div>
        )}
      </div>

      <Link
        to={`/jobs/${job.id}`}
        className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 font-medium text-foreground hover:bg-primary/90"
      >
        Open Job Detail
      </Link>
    </div>
  );
}

function Row({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 rounded-lg bg-card px-3 py-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-foreground">{value}</p>
      </div>
    </div>
  );
}
